import { PipeTransform, Type } from '@angular/core';
import { GenericTableColumn } from '@shared/models/generictablecolumn';

/** What `MatTableDataSource` asks for the value a row sorts by in a column: the shape of its `sortingDataAccessor`. */
export type SortingDataAccessor = (row: any, sortHeaderId: string) => string | number;

/** What the accessor needs from the table it sorts for. Kept as functions, so the accessor reads the columns and the pipes as they are when a sort happens, not as they were when it was made. */
export interface SortingDependencies {
	/** The columns the table shows, by which the sort header id (a `dataKey`) is looked up. */
	readonly columns: () => ReadonlyArray<GenericTableColumn>;
	/** The pipe instance for a column's `pipe`, the same one the template renders with. */
	readonly pipeFor: (pipe: Type<PipeTransform>) => PipeTransform;
	/** Translates a key, for columns marked `isTranslatable`. */
	readonly translate: (key: string) => string;
	/** The accessor the data source had before: Material's default, or one the host of the table set itself. Everything this accessor does not decide is left to it. */
	readonly inherited: SortingDataAccessor;
}

/** A value under a key that may be dotted, the way the table's cells read one: `item.id` is `row.item.id`. */
function valueAt(row: any, key: string): unknown {
	return key.split('.').reduce((value: any, segment) => (value == null ? value : value[segment]), row);
}

/**
 * The text a column shows for a row, without the markup the cell may wrap it in.
 *
 * Only what changes the text is applied: the translation for a translatable column, the pipe for a column with one. Links, images and number formats are left out, since a column that asks to sort as shown asks for the words on screen, not the markup around them — see {@link sortingDataAccessorFor}.
 */
function shownText(row: any, column: GenericTableColumn, { pipeFor, translate }: SortingDependencies): string {
	let value = valueAt(row, column.dataKey) ?? column.defaultValue ?? '';
	// nothing is not a key to translate: the translate service refuses an empty one
	if (column.isTranslatable && value !== '') {
		value = translate(String(value));
	}
	if (column.pipe) {
		value = pipeFor(column.pipe).transform(value, ...(column.pipeArgs ?? []));
	}
	return String(value ?? '');
}

/**
 * The `sortingDataAccessor` for a table of {@link GenericTableColumn}s: a column sorts by the value its `sortKey` names, or by what the user sees when it asks to, and otherwise as it always did.
 *
 * `MatTableDataSource` sorts by the raw value under the sort header id, which is the column's `dataKey`. That is wrong in two ways the generic table meets. A column that shows a formatted date holds the text, so it sorts day, month, year; its `sortKey` names the timestamp kept beside the text, and that is what is
 * returned. A column that shows a name holds an id and shows the name through a pipe, or holds the name in whatever case it came in; with `sortAsShown` it sorts by the text on screen, pipe and translation applied, folded to lower case so the order does not split on capitals.
 *
 * A `sortKey` wins over `sortAsShown` when a column has both, since a value kept for sorting is more exact than any text made from it. Sorting as shown is asked for, column by column, and never assumed from a pipe: a host may well want a piped column sorted by the raw value underneath — a status slug shown translated,
 * say — and may have set an accessor of its own for it. Every column that asks for neither is left to the accessor the data source already had. That keeps Material's own handling of numbers and numeric strings, and it keeps an accessor a host set on its own data source before handing it to the table.
 */
export function sortingDataAccessorFor(dependencies: SortingDependencies): SortingDataAccessor {
	const { columns, inherited } = dependencies;
	return (row, sortHeaderId) => {
		const column = columns().find((candidate) => candidate.dataKey === sortHeaderId);
		if (column === undefined) {
			return inherited(row, sortHeaderId);
		}
		if (column.sortKey) {
			return inherited(row, column.sortKey);
		}
		if (column.sortAsShown) {
			return shownText(row, column, dependencies).toLocaleLowerCase();
		}
		return inherited(row, sortHeaderId);
	};
}

/** What `MatTableDataSource` asks whether a row matches a free-text filter: the shape of its `filterPredicate`. */
export type FilterPredicate = (row: any, filter: string) => boolean;

/**
 * The `filterPredicate` for a table whose rows carry values that are only there to sort by.
 *
 * Material's default predicate matches the filter against every value on the row, and a timestamp kept beside a formatted date — see `GenericTableColumn.sortKey` — is a thirteen-digit number that a search for a year or a day would match in almost every row. So the row is handed on without the values the columns'
 * `sortKey`s name, and the predicate the data source already had decides on the rest, exactly as before.
 */
export function filterPredicateFor(columns: () => ReadonlyArray<GenericTableColumn>, inherited: FilterPredicate): FilterPredicate {
	return (row, filter) => {
		const sortKeys = columns().flatMap((column) => (column.sortKey ? [column.sortKey] : []));
		if (sortKeys.length === 0 || row == null || typeof row !== 'object') {
			return inherited(row, filter);
		}
		const shown = { ...row };
		for (const sortKey of sortKeys) {
			delete shown[sortKey];
		}
		return inherited(shown, filter);
	};
}
